import { API_HOST_WMS } from "config/api.config";
import { api } from "network/network";
import { APIendpoints } from "utils/constants/APIendpoints";
import { getUserRoleConfig, normalizeUserRole } from "./userRoles";

const getInitials = (name) => {
  const initials = String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  return initials || "U";
};

export const formatUserPhone = (phoneNumber = "") => {
  const value = String(phoneNumber).trim();
  const digits = value.replace(/\D/g, "");
  const localNumber =
    digits.length === 12 && digits.startsWith("91") ? digits.slice(2) : digits;

  if (localNumber.length !== 10) return value;

  return `+91 ${localNumber.slice(0, 5)} ${localNumber.slice(5)}`;
};

export const maskUserPhone = (phoneNumber = "") => {
  const value = String(phoneNumber).trim();
  const digits = value.replace(/\D/g, "");
  const localNumber =
    digits.length === 12 && digits.startsWith("91") ? digits.slice(2) : digits;

  if (localNumber.length !== 10) return value;

  return `+91 ${localNumber.slice(0, 3)}****${localNumber.slice(-3)}`;
};

export const normalizeUserContacts = ({ email = "", phone = "" } = {}) => {
  const contacts = [email, phone]
    .map((contact) => String(contact).trim())
    .filter(Boolean);
  const normalizedEmail =
    contacts.find((contact) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact)) ||
    "";
  const normalizedPhone =
    contacts.find((contact) => {
      const digits = contact.replace(/\D/g, "");
      return (
        digits.length === 10 ||
        (digits.length === 12 && digits.startsWith("91"))
      );
    }) || "";

  return {
    email: normalizedEmail,
    phone: formatUserPhone(normalizedPhone),
  };
};

export const getUserRoleForOutlet = (sellerOutlets, outletId) => {
  if (
    !Array.isArray(sellerOutlets) ||
    outletId === null ||
    outletId === undefined
  ) {
    return getUserRoleConfig();
  }

  const matchingOutlet = sellerOutlets.find(
    (outlet) => String(outlet.outlet_id) === String(outletId)
  );

  return getUserRoleConfig(matchingOutlet?.role);
};

export const normalizeOutletUser = (mapping = {}, outletId) => {
  const sellerOutlets = Array.isArray(mapping.seller_outlets)
    ? mapping.seller_outlets
    : [];
  const roleConfig = getUserRoleForOutlet(sellerOutlets, outletId);
  const contacts = normalizeUserContacts({
    email: mapping.email,
    phone: mapping.phone_number,
  });

  return {
    id: mapping.user_id,
    name: mapping.name || "Unnamed user",
    initials: getInitials(mapping.name),
    role: roleConfig?.label || "",
    roleTone: roleConfig?.tone || "",
    email: contacts.email,
    phone: contacts.phone,
    isAdmin:
      Boolean(mapping.is_admin) ||
      sellerOutlets.some(
        (outlet) =>
          String(outlet.outlet_id) === String(outletId) &&
          normalizeUserRole(outlet.role) === "ADMIN"
      ),
    sellerOutlets,
  };
};

export const normalizeOutletUsersResponse = (response, outletId) => {
  const mappings =
    response?.data?.response?.user_to_seller_outlet_mappings || [];

  return Array.isArray(mappings)
    ? mappings
        .map((mapping) => normalizeOutletUser(mapping, outletId))
        .sort((firstUser, secondUser) =>
          firstUser.isAdmin === secondUser.isAdmin
            ? 0
            : firstUser.isAdmin
            ? -1
            : 1
        )
    : [];
};

// In-memory cache of an outlet's users so returning to the list (e.g. after
// cancelling the add flow) reuses it instead of refetching. It is invalidated
// after a user is added, and is naturally reset on reload / outlet switch
// (an outlet switch reloads the page, clearing this module state).
let usersCache = { outletId: null, users: null };

export const getCachedOutletUsers = (outletId) =>
  usersCache.outletId !== null && usersCache.outletId === String(outletId)
    ? usersCache.users
    : null;

export const setCachedOutletUsers = (outletId, users) => {
  usersCache = { outletId: String(outletId), users };
};

export const invalidateOutletUsersCache = () => {
  usersCache = { outletId: null, users: null };
};

export const getOutletUsers = async (outletId) => {
  if (!outletId) {
    throw new Error("No outlet is selected.");
  }

  const response = await api.get(
    API_HOST_WMS,
    APIendpoints.sellerOutletToUserMapping,
    { query: { outlet_id: outletId } }
  );

  return normalizeOutletUsersResponse(response, outletId);
};

export const getOutletUsersErrorMessage = (error) =>
  error?.response?.data?.error?.message ||
  error?.message ||
  "We couldn't load users for this outlet.";
